import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  frameMessage,
  type FleetMapErrorCode,
  type FleetMapFrameMessage,
} from '@bonbon/types';
import { ALLOWED_HOST_ORIGINS } from './config';
import {
  cspViolationKey,
  MAX_FORWARDED_VIOLATIONS,
  toCspViolation,
} from './csp';
import { readHostEvent, readyTargetOrigins } from './hostBridge';
import { INITIAL_MAP_STATE, reduceMapState, type MapState } from './mapState';

/** Messages held until the host has proved who it is, then sent in order. */
const MAX_PENDING_MESSAGES = 20;

export interface HostBridge {
  state: MapState;
  /** A click on a van (or the empty map): select it here and tell the host. */
  select: (telematicsVehicleId: string | null) => void;
  /** A click on a stop (or, with null, on the map while a stop is selected). */
  selectStop: (stopId: string | null) => void;
  reportError: (code: FleetMapErrorCode) => void;
}

/**
 * The only door between this page and the ERP. It listens for the host's
 * messages (checking sender and origin on each), announces `ready`, and
 * replies to the verified origin only. It sends nothing to a page that has not
 * itself sent a valid message first, and renders nothing until `init`.
 */
export function useHostBridge(
  allowedOrigins: readonly string[] = ALLOWED_HOST_ORIGINS,
): HostBridge {
  const [state, dispatch] = useReducer(reduceMapState, INITIAL_MAP_STATE);
  const hostOrigin = useRef<string | null>(null);
  const pending = useRef<FleetMapFrameMessage[]>([]);

  const post = useCallback((message: FleetMapFrameMessage): void => {
    const origin = hostOrigin.current;
    if (origin === null) {
      if (pending.current.length < MAX_PENDING_MESSAGES) {
        pending.current.push(message);
      }
      return;
    }
    window.parent.postMessage(message, origin);
  }, []);

  useEffect(() => {
    const hostWindow = window.parent;

    const onMessage = (event: MessageEvent): void => {
      const trusted = readHostEvent(event, hostWindow, allowedOrigins);
      if (trusted === null) return;
      if (hostOrigin.current !== trusted.origin) {
        hostOrigin.current = trusted.origin;
        for (const queued of pending.current.splice(0)) {
          hostWindow.postMessage(queued, trusted.origin);
        }
      }
      dispatch({ kind: 'host', message: trusted.message });
    };

    const forwarded = new Set<string>();
    const onViolation = (event: SecurityPolicyViolationEvent): void => {
      const violation = toCspViolation(event);
      const key = cspViolationKey(violation);
      if (forwarded.has(key) || forwarded.size >= MAX_FORWARDED_VIOLATIONS) return;
      forwarded.add(key);
      post(frameMessage('cspViolation', violation));
    };

    window.addEventListener('message', onMessage);
    document.addEventListener('securitypolicyviolation', onViolation);

    // Typed as always present, but Firefox has none.
    const ancestors: DOMStringList | undefined = window.location.ancestorOrigins;
    const targets = readyTargetOrigins(
      ancestors === undefined ? undefined : Array.from(ancestors),
      allowedOrigins,
      hostWindow !== window,
    );
    for (const target of targets) {
      hostWindow.postMessage(frameMessage('ready', {}), target);
    }

    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('securitypolicyviolation', onViolation);
    };
  }, [allowedOrigins, post]);

  const select = useCallback(
    (telematicsVehicleId: string | null): void => {
      dispatch({ kind: 'select', telematicsVehicleId });
      post(frameMessage('vehicleSelected', { telematicsVehicleId }));
    },
    [post],
  );

  const selectStop = useCallback(
    (stopId: string | null): void => {
      dispatch({ kind: 'selectStop', stopId });
      post(frameMessage('stopSelected', { stopId }));
    },
    [post],
  );

  const reportError = useCallback(
    (code: FleetMapErrorCode): void => {
      post(frameMessage('error', { code }));
    },
    [post],
  );

  return { state, select, selectStop, reportError };
}
